import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import {
  ACCEPTED_IMAGE_TYPES,
  classifyUploadFile,
  replaceProjectImage,
  uploadProjectFile,
} from '../studio/projectImages';
import './production.css';

function sizeLabel(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isoStamp(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  try {
    return new Date(value).toISOString();
  } catch {
    return String(value);
  }
}

function formatUserDate(value) {
  if (!value) return 'Unknown';
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
}

function fullAccessibleDate(value) {
  if (!value) return 'Unknown date';
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'full',
      timeStyle: 'long',
    });
  } catch {
    return String(value);
  }
}

function fileCapabilities(file) {
  const actions = file.actions || {};
  return {
    isProtected: Boolean(file.is_protected ?? actions.is_protected),
    protectionReason: file.protection_reason || actions.protection_reason || '',
    canEdit: Boolean(actions.can_edit ?? actions.edit),
    canMove: Boolean(actions.can_move ?? actions.can_edit ?? actions.edit),
    canReplace: Boolean(actions.can_replace ?? actions.replace),
    canDelete: Boolean(actions.can_delete ?? actions.delete),
    reasons: actions.reasons || {},
  };
}

function uploadTargetFolderId(folderId) {
  if (folderId === 'root' || folderId === '') return null;
  if (!folderId) return null;
  return folderId;
}

function previewFallbackLabel(file) {
  if (file.kind === 'archive' || file.preview_kind === 'archive_icon') return 'ZIP';
  const ext = (file.extension || '').toUpperCase();
  if (ext) return ext;
  return 'FILE';
}

let uploadSeq = 0;

export default function ProjectFilesPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [data, setData] = useState(null);
  const [folders, setFolders] = useState([]);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [folderId, setFolderId] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [editFile, setEditFile] = useState(null);
  const [editName, setEditName] = useState('');
  const [editFolder, setEditFolder] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleteFile, setDeleteFile] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceProgress, setReplaceProgress] = useState(null);
  const [replaceError, setReplaceError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [uploads, setUploads] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const replaceInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const base = `/console/projects/${projectId}`;

  useEffect(() => {
    let live = true;
    setData(null); setError('');
    const timer = setTimeout(() => {
      const fileParams = { q: query, kind, page };
      if (folderId === 'root') fileParams.folder_id = '';
      else if (folderId) fileParams.folder_id = folderId;
      Promise.all([
        bakingApi.getProject(projectId),
        bakingApi.listProjectFiles(projectId, fileParams),
        bakingApi.listProjectFolders(projectId, { parent_id: 'all' }).catch(() => ({ results: [] })),
      ])
        .then(([p, files, folderPayload]) => {
          if (!live) return;
          setProject(p);
          setData(files);
          setFolders(folderPayload.results || []);
        })
        .catch(e => { if (live) setError(e.message || 'Could not load project files.'); });
    }, 200);
    return () => { live = false; clearTimeout(timer); };
  }, [projectId, query, kind, folderId, page, reload]);

  const folderName = (id) => {
    if (!id) return 'Root';
    const match = folders.find(f => f.id === id);
    return match ? match.name : 'Folder';
  };

  const uploadDestinationLabel = () => {
    if (folderId === 'root' || !folderId) return 'project root';
    return `folder “${folderName(folderId)}”`;
  };

  const updateUpload = (id, patch) => {
    setUploads((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const enqueueFiles = (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    const targetFolder = uploadTargetFolderId(folderId === '' ? 'root' : folderId);
    const next = chosen.map((file) => {
      uploadSeq += 1;
      const classified = classifyUploadFile(file);
      return {
        id: `u-${uploadSeq}`,
        file,
        name: file.name,
        progress: 0,
        status: classified.error ? 'failed' : 'queued',
        error: classified.error || '',
        kind: classified.kind,
        targetFolder,
      };
    });
    setUploads((rows) => [...next, ...rows]);
    next.forEach((item) => {
      if (item.status === 'failed') return;
      runUpload(item);
    });
  };

  const runUpload = async (item) => {
    updateUpload(item.id, { status: 'uploading', progress: 0, error: '' });
    try {
      const clientModifiedAt = item.file.lastModified
        ? new Date(item.file.lastModified).toISOString()
        : undefined;
      await uploadProjectFile(bakingApi, projectId, item.file, {
        folderId: item.targetFolder,
        clientModifiedAt,
        onProgress: (progress) => updateUpload(item.id, { progress }),
      });
      updateUpload(item.id, { status: 'success', progress: 100, error: '' });
      const hiddenByFilter =
        (kind && item.kind && kind !== item.kind) ||
        (folderId === 'root' && item.targetFolder) ||
        (folderId && folderId !== 'root' && folderId !== '' && item.targetFolder !== folderId);
      setActionNotice(
        hiddenByFilter
          ? `Uploaded “${item.name}” to ${item.targetFolder ? folderName(item.targetFolder) : 'Root'}. It may be hidden by the current search or filters.`
          : `Uploaded “${item.name}”.`
      );
      setReload((n) => n + 1);
    } catch (e) {
      updateUpload(item.id, {
        status: 'failed',
        error: e.message || 'Upload failed.',
        progress: item.progress || 0,
      });
    }
  };

  const openEdit = (file) => {
    const caps = fileCapabilities(file);
    if (!caps.canEdit) {
      setActionNotice(caps.reasons.edit || caps.protectionReason || 'This file is protected.');
      return;
    }
    setEditFile(file);
    setEditName(file.name || '');
    setEditFolder(file.folder_id || '');
    setEditError('');
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    if (!editFile) return;
    setEditBusy(true);
    setEditError('');
    try {
      const body = {
        original_filename: editName.trim(),
        folder_id: editFolder || null,
        expected_updated_at: isoStamp(editFile.updated_at),
      };
      if (editFile.kind === 'archive') {
        await bakingApi.updateProjectRevisionFile(projectId, editFile.id, body);
      } else {
        await bakingApi.updateProjectImage(projectId, editFile.id, body);
      }
      setEditFile(null);
      setActionNotice(`Updated “${editName.trim()}”.`);
      setReload(n => n + 1);
    } catch (e) {
      setEditError(e.message || 'Could not save changes.');
      setReload(n => n + 1);
    } finally {
      setEditBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteFile) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const body = { expected_updated_at: isoStamp(deleteFile.updated_at) };
      if (deleteFile.kind === 'archive') {
        await bakingApi.deleteProjectRevisionFile(projectId, deleteFile.id, body);
      } else {
        await bakingApi.deleteProjectImage(projectId, deleteFile.id, body);
      }
      setDeleteFile(null);
      setActionNotice(`Deleted “${deleteFile.name}”.`);
      setReload(n => n + 1);
    } catch (e) {
      setDeleteError(e.message || 'Could not delete this file.');
      setReload(n => n + 1);
    } finally {
      setDeleteBusy(false);
    }
  };

  const startReplace = (file) => {
    const caps = fileCapabilities(file);
    if (!caps.canReplace) {
      setActionNotice(caps.reasons.replace || caps.protectionReason || 'This file cannot be replaced. Upload a separate file instead.');
      return;
    }
    setReplaceFile(file);
    setReplaceError('');
    setReplaceProgress(null);
    setTimeout(() => replaceInputRef.current?.click(), 0);
  };

  const onReplaceChosen = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !replaceFile) return;
    setReplaceProgress(0);
    setReplaceError('');
    try {
      await replaceProjectImage(bakingApi, projectId, replaceFile.id, file, {
        expectedUpdatedAt: isoStamp(replaceFile.updated_at),
        onProgress: setReplaceProgress,
      });
      setActionNotice(`Replaced “${replaceFile.name}”.`);
      setReplaceFile(null);
      setReplaceProgress(null);
      setReload(n => n + 1);
    } catch (e) {
      setReplaceError(e.message || 'Replacement failed. The original file is unchanged.');
      setReplaceProgress(null);
      setReload(n => n + 1);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    enqueueFiles(event.dataTransfer?.files);
  };

  return <div className="production-page">
    <Link to={base}>← Project</Link>
    <header className="production-header"><div>
      <p className="production-eyebrow">{project?.name || 'PROJECT WORKSPACE'}</p>
      <h1>Project Files</h1><p>Upload references and project archives, then manage them in one place.</p>
    </div>
      <div className="d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-primary" onClick={() => uploadInputRef.current?.click()}>Upload</button>
        <button type="button" className="btn btn-outline-secondary" onClick={() => setReload(n => n + 1)}>Refresh</button>
      </div>
    </header>

    <section
      className={`project-files-dropzone${dragActive ? ' is-active' : ''}`}
      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
      onDrop={onDrop}
      aria-label="Upload files"
    >
      <p><strong>Drop files here</strong> or use Upload. Files go to the {uploadDestinationLabel()}.</p>
      <p className="project-files-hint">Supported: PNG, JPEG, WebP images (up to 10 MB) and ZIP project archives (up to 250 MB). Server verification decides the final type.</p>
      <input
        ref={uploadInputRef}
        type="file"
        multiple
        accept={[...ACCEPTED_IMAGE_TYPES, '.zip', 'application/zip'].join(',')}
        className="visually-hidden"
        aria-label="Choose files to upload"
        onChange={(e) => { enqueueFiles(e.target.files); e.target.value = ''; }}
      />
    </section>

    {uploads.length ? <section className="project-files-uploads" aria-label="Upload progress">
      <h2 className="visually-hidden">Uploads</h2>
      <ul>
        {uploads.map((row) => (
          <li key={row.id}>
            <span>{row.name}</span>
            {row.status === 'uploading' ? <span role="status">Uploading… {row.progress}%</span> : null}
            {row.status === 'success' ? <span role="status">Uploaded</span> : null}
            {row.status === 'queued' ? <span role="status">Waiting…</span> : null}
            {row.status === 'failed' ? <span role="alert">{row.error || 'Failed'}</span> : null}
            {row.status === 'failed' ? (
              <button type="button" className="btn btn-link btn-sm" onClick={() => runUpload(row)}>Retry</button>
            ) : null}
            <button type="button" className="btn btn-link btn-sm" onClick={() => setUploads((rows) => rows.filter((r) => r.id !== row.id))}>Dismiss</button>
          </li>
        ))}
      </ul>
    </section> : null}

    <div className="project-files-filters">
      <label>Search files<input className="form-control" type="search" value={query} placeholder="File name, character, or story" onChange={e => { setQuery(e.target.value); setPage(1); }} /></label>
      <label>File type<select className="form-select" value={kind} onChange={e => { setKind(e.target.value); setPage(1); }}><option value="">All files</option><option value="reference">Reference images</option><option value="archive">Project archives</option></select></label>
      <label>Folder<select className="form-select" value={folderId} onChange={e => { setFolderId(e.target.value); setPage(1); }}>
        <option value="">All folders</option>
        <option value="root">Root (no folder)</option>
        {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select></label>
    </div>
    <p className="production-footnote">Protected files are referenced by workflows, history, or jobs and cannot be edited, moved, replaced, or deleted. Upload a separate file when you need different content. Spaces versioning may retain prior object versions after deletion. Thumbnails are platform-managed and do not appear as separate files.</p>
    {actionNotice ? <p role="status" className="project-files-notice">{actionNotice} <button type="button" className="btn btn-link btn-sm" onClick={() => setActionNotice('')}>Dismiss</button></p> : null}
    {error ? <div role="alert" className="alert alert-danger">{error} <button className="btn btn-outline-danger btn-sm" onClick={() => setReload(n => n + 1)}>Try again</button></div> : !data ? <p role="status">Loading files…</p> : <>
      <p role="status">{data.count} {data.count === 1 ? 'file' : 'files'}{query || kind || folderId ? ' matching your filters' : ''}</p>
      {!data.results.length ? <section className="production-empty"><span aria-hidden="true">▤</span><h2>{query || kind || folderId ? 'No matching files' : 'No uploaded files yet'}</h2><p>{query || kind || folderId ? 'Try a different search, folder, or file type.' : 'Upload a PNG, JPEG, WebP, or ZIP archive to get started.'}</p></section> : <div className="project-files-grid">{data.results.map(file => {
        const caps = fileCapabilities(file);
        const workflowHref = file.record_id && file.department
          ? `${base}/departments/${file.department}/${file.record_id}`
          : null;
        const typeLabel = file.type_label || (file.kind === 'archive' ? 'ZIP archive' : 'File');
        const showUpdated =
          file.updated_at &&
          file.uploaded_at &&
          isoStamp(file.updated_at) !== isoStamp(file.uploaded_at) &&
          file.date_label !== 'Workflow updated';
        return <article className="project-file-card" key={file.id}>
          <div className="project-file-preview">
            {file.preview ? (
              <img src={file.preview} alt={file.name} loading="lazy" />
            ) : (
              <span aria-hidden="true">{file.preview_status === 'pending' ? '…' : previewFallbackLabel(file)}</span>
            )}
            {file.preview_status === 'pending' ? <span className="visually-hidden">Thumbnail pending</span> : null}
          </div>
          <div className="project-file-details">
            <p className="production-eyebrow">{typeLabel} · {folderName(file.folder_id)}</p>
            <h2>{file.name}</h2>
            {caps.isProtected ? <p className="project-file-protected" role="status"><span className="project-file-protected-badge">Protected</span> {caps.protectionReason}</p> : null}
            <p>{file.source}</p>
            <p>{sizeLabel(file.byte_size)} · {file.extension ? `.${file.extension}` : typeLabel} · {String(file.status || '').replace(/_/g, ' ')}</p>
            <p>
              <span title={fullAccessibleDate(file.uploaded_at || file.date)}>
                {file.date_label || 'Uploaded'}: {formatUserDate(file.uploaded_at || file.date)}
              </span>
            </p>
            {showUpdated ? (
              <p title={fullAccessibleDate(file.updated_at)}>Updated: {formatUserDate(file.updated_at)}</p>
            ) : null}
            {file.status === 'deleting' ? <p role="status" className="project-files-pending">Deletion in progress…</p> : null}
            <div className="project-file-actions d-flex flex-wrap gap-2">
              {file.preview && file.preview_kind !== 'thumbnail' ? (
                <a className="btn btn-sm btn-outline-primary" href={file.preview} download={file.name}>Download image</a>
              ) : null}
              <Link className="btn btn-sm btn-outline-secondary" to={workflowHref || `${base}/settings`}>{workflowHref ? 'Open workflow' : 'View archive details'}</Link>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!caps.canEdit} title={caps.reasons.edit || caps.protectionReason || ''} aria-disabled={!caps.canEdit} onClick={() => openEdit(file)}>Edit</button>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!caps.canReplace} title={caps.reasons.replace || caps.protectionReason || ''} aria-disabled={!caps.canReplace} onClick={() => startReplace(file)}>Replace</button>
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={!caps.canDelete} title={caps.reasons.delete || caps.protectionReason || ''} aria-disabled={!caps.canDelete} onClick={() => { setDeleteFile(file); setDeleteError(''); }}>Delete</button>
            </div>
            {caps.isProtected ? <p className="project-files-hint">Upload a separate file when you need different content.</p> : null}
          </div>
        </article>;
      })}</div>}
      {(page > 1 || data.has_next) && <nav className="project-files-pagination" aria-label="File pages"><button className="btn btn-outline-secondary" disabled={page === 1} onClick={() => setPage(n => n - 1)}>Previous</button><span>Page {page}</span><button className="btn btn-outline-secondary" disabled={!data.has_next} onClick={() => setPage(n => n + 1)}>Next</button></nav>}
    </>}

    <input ref={replaceInputRef} type="file" accept={ACCEPTED_IMAGE_TYPES.join(',')} className="visually-hidden" aria-hidden="true" onChange={onReplaceChosen} />

    {editFile ? <div className="project-files-dialog-backdrop" role="presentation" onClick={() => !editBusy && setEditFile(null)}>
      <form className="project-files-dialog" role="dialog" aria-modal="true" aria-labelledby="edit-file-title" onClick={e => e.stopPropagation()} onSubmit={submitEdit}>
        <h2 id="edit-file-title">Edit file</h2>
        <p>Changes update the database entry only. The storage key stays the same.</p>
        <label>Display filename<input className="form-control" value={editName} onChange={e => setEditName(e.target.value)} required /></label>
        <label>Folder<select className="form-select" value={editFolder} onChange={e => setEditFolder(e.target.value)}>
          <option value="">Root (no folder)</option>
          {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select></label>
        {editError ? <div role="alert" className="alert alert-danger">{editError}</div> : null}
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <button type="button" className="btn btn-outline-secondary" disabled={editBusy} onClick={() => setEditFile(null)}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={editBusy}>{editBusy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div> : null}

    {deleteFile ? <div className="project-files-dialog-backdrop" role="presentation" onClick={() => !deleteBusy && setDeleteFile(null)}>
      <div className="project-files-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-file-title" onClick={e => e.stopPropagation()}>
        <h2 id="delete-file-title">Delete file?</h2>
        <p>Delete <strong>{deleteFile.name}</strong>? This removes the project file record and schedules deletion of the stored object. Protected files referenced by workflows or jobs cannot be deleted.</p>
        {deleteError ? <div role="alert" className="alert alert-danger">{deleteError}</div> : null}
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <button type="button" className="btn btn-outline-secondary" disabled={deleteBusy} onClick={() => setDeleteFile(null)}>Cancel</button>
          <button type="button" className="btn btn-danger" disabled={deleteBusy} onClick={submitDelete}>{deleteBusy ? 'Deleting…' : 'Delete'}</button>
        </div>
      </div>
    </div> : null}

    {(replaceProgress != null || replaceError) ? <div className="project-files-dialog-backdrop" role="presentation">
      <div className="project-files-dialog" role="dialog" aria-modal="true" aria-labelledby="replace-file-title">
        <h2 id="replace-file-title">Replace file</h2>
        {replaceProgress != null ? <p role="status">Uploading… {replaceProgress}%</p> : null}
        {replaceError ? <div role="alert" className="alert alert-danger">{replaceError}</div> : null}
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <button type="button" className="btn btn-outline-secondary" onClick={() => { setReplaceFile(null); setReplaceError(''); setReplaceProgress(null); }}>Close</button>
        </div>
      </div>
    </div> : null}
  </div>;
}
